import { useState, useEffect, useCallback } from "react";
import {
  Page,
  Card,
  Badge,
  Button,
  EmptyState,
  Spinner,
  Banner,
  BlockStack,
  InlineStack,
  Text,
  Box,
  Divider,
} from "@shopify/polaris";
import {
  CircleChevronRightIcon,
  ViewIcon,
} from "@shopify/polaris-icons";
import { useNavigate } from "react-router-dom";
import { getGames, type Game } from "../api/campaigns";

function GamesCatalogPage() {
  const navigate = useNavigate();
  const [games, setGames] = useState<Game[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadGames = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getGames();
      setGames(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load games");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadGames();
  }, [loadGames]);

  const getStatusBadge = (status: Game["status"]) => {
    switch (status) {
      case "ACTIVE":
        return <Badge tone="success">Available</Badge>;
      case "HIDDEN":
        return <Badge tone="warning">Hidden</Badge>;
      case "DEPRECATED":
        return <Badge tone="info">Deprecated</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  if (loading) {
    return (
      <Page title="Games">
        <Card>
          <Box padding="800">
            <BlockStack align="center" gap="400">
              <Spinner size="large" />
              <Text as="p" variant="bodyMd" tone="subdued">Loading games...</Text>
            </BlockStack>
          </Box>
        </Card>
      </Page>
    );
  }

  if (error) {
    return (
      <Page title="Games">
        <Banner tone="critical" onDismiss={() => setError(null)}>
          {error}
        </Banner>
        <Card>
          <Box padding="800">
            <BlockStack align="center" gap="400">
              <Text as="p">Unable to load games. Please try again.</Text>
              <Button onClick={loadGames}>Retry</Button>
            </BlockStack>
          </Box>
        </Card>
      </Page>
    );
  }

  if (games.length === 0) {
    return (
      <Page title="Games">
        <Card>
          <EmptyState
            heading="No games available"
            image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
          >
            <p>
              Games will appear here once they are registered in the system.
            </p>
          </EmptyState>
        </Card>
      </Page>
    );
  }

  return (
    <Page
      title="Games"
      subtitle={`${games.length} game${games.length !== 1 ? "s" : ""} available`}
    >
      <BlockStack gap="400">
        {games.map((game) => (
          <Card key={game.id}>
            <Box padding="500">
              <BlockStack gap="400">
                <InlineStack align="space-between" blockAlign="start">
                  <BlockStack gap="200">
                    <InlineStack gap="300" blockAlign="center">
                      <Text as="h2" variant="headingMd" fontWeight="semibold">
                        {game.name}
                      </Text>
                      {getStatusBadge(game.status)}
                    </InlineStack>
                    {game.description && (
                      <Text as="p" variant="bodyMd" tone="subdued">
                        {game.description}
                      </Text>
                    )}
                  </BlockStack>
                </InlineStack>

                {game.tags && game.tags.length > 0 && (
                  <InlineStack gap="200">
                    {game.tags.map((tag) => (
                      <Badge key={tag}>{tag}</Badge>
                    ))}
                  </InlineStack>
                )}

                {game.versions && game.versions.length > 0 && (
                  <>
                    <Divider />
                    <InlineStack align="space-between" blockAlign="center">
                      <BlockStack gap="100">
                        <Text as="p" variant="bodySm" tone="subdued">
                          Version {game.versions[0]?.version}
                        </Text>
                        {game.versions[0]?.placements && (
                          <Text as="p" variant="bodySm" tone="subdued">
                            Placements: {game.versions[0].placements.join(", ")}
                          </Text>
                        )}
                      </BlockStack>
                      <InlineStack gap="200">
                        <Button
                          variant="tertiary"
                          icon={ViewIcon}
                          onClick={() => navigate("/playground")}
                        >
                          Preview
                        </Button>
                        <Button
                          variant="primary"
                          icon={CircleChevronRightIcon}
                          onClick={() => navigate(`/campaigns/new?gameId=${game.id}`)}
                        >
                          Use in Campaign
                        </Button>
                      </InlineStack>
                    </InlineStack>
                  </>
                )}
              </BlockStack>
            </Box>
          </Card>
        ))}

        {/* Coming soon section */}
        <Card>
          <Box padding="400">
            <InlineStack align="center">
              <Text as="p" variant="bodySm" tone="subdued">
                More games coming soon: Scratch Cards, Slot Machine, Quiz, and more.
              </Text>
            </InlineStack>
          </Box>
        </Card>
      </BlockStack>
    </Page>
  );
}

export default GamesCatalogPage;
